import { Card } from '../../ui'
import type {
  ChangeList,
  ChangedFinding,
  ExecutiveAction,
  ReportSummary,
  ScanFreshness,
  UnreadControl,
} from '../../lib/api'
import { formatDate } from './LiveFindings'
import {
  controlCount,
  controls,
  listOf,
  noLicenceFor,
  notLicensedAreaCount,
  notLicensedAreas,
  notLicensedIn,
  partlyNotLicensedAreas,
} from './notLicensed'
import { checkName, partlyReadAreas, partlyReadNote, unconfirmedAreas } from './partlyRead'
import type { PartlyReadArea } from './partlyRead'
import { describeGroup, GROUP_ORDER, groupOf, isWaiting } from './unreadReasons'
import type { UnreadGroupId } from './unreadReasons'

/*
 * THE EXECUTIVE FIRST PAGE (Codex review, 2026-09-27).
 *
 * The printed report was built for an auditor, and a reader who has a minute got the same document.
 * This is the page they read: the actions to take first, what changed since the assessment before,
 * who owns each action, and what this assessment could not fully cover. It prints first and alone
 * on its page; the header, the summary, the findings and the sealed record follow it unchanged.
 *
 * ONLY WHAT THE PRODUCT HAS. The actions are the Recommendations page's own order. The line under
 * each is quoted from the catalogue's "What this means", never written here. The owner is the
 * finding's working-state owner, and "Unassigned" only where nobody holds it. There is no target
 * date, because a finding has none, so none is shown and no column says "not tracked". Coverage is
 * the same scan the header dates. The not-licensed, partly-read, unconfirmed-record and unread lines
 * are in the dashboard's words (notLicensed.ts, partlyRead.ts, unreadReasons.ts); the line for an
 * area that did not complete is this page's own.
 */

/** Levels in reading order, worst first. */
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Informational'] as const

/** How many findings each change row names before "and N more". The counts are exact regardless. */
export const NAMES_SHOWN = 5

/** The summary read, as the page holds it: still loading, not usable, or ready for this workspace. */
export type SummaryLoad =
  | { state: 'loading' }
  | { state: 'unavailable' }
  | { state: 'ready'; data: ReportSummary }

/**
 * Who owns an action, in words.
 *
 * "Unassigned" only when nobody is recorded. An owner the workspace can no longer see (they left)
 * is still an owner, and an owner who is no longer an active member is named with that said.
 */
export function ownerText(owner: ExecutiveAction['owner']): string {
  if (!owner.assigned) return 'Unassigned'
  if (!owner.email) return 'Assigned to someone no longer in this workspace'
  return owner.activeMember ? owner.email : `${owner.email} (no longer an active member)`
}

/** "12 findings are open: 2 Critical, 7 High and 3 Medium." with set-aside findings said apart. */
export function openFindingsSentence(bySeverity: Record<string, number>, suppressed: number): string {
  const total = Object.values(bySeverity).reduce((sum, n) => sum + n, 0)
  const setAside = suppressed > 0
    ? ` ${suppressed} more ${suppressed === 1 ? 'is' : 'are'} set aside and not counted here.`
    : ''

  if (total === 0) {
    return 'No findings are open. That is what these checks found, not a statement that nothing is '
      + `there.${setAside}`
  }

  const known = new Set<string>(SEVERITIES)
  const parts = [
    ...SEVERITIES.filter((level) => (bySeverity[level] ?? 0) > 0),
    // A level this build does not know is still counted in the total, so it is named too.
    ...Object.keys(bySeverity).filter((level) => !known.has(level) && (bySeverity[level] ?? 0) > 0),
  ].map((level) => `${bySeverity[level]} ${level}`)

  return `${total} ${total === 1 ? 'finding is' : 'findings are'} open: ${listOf(parts)}.${setAside}`
}

/** One area this assessment did not fully cover, and each way it did not. */
export interface CoverageGap {
  area: string
  notes: string[]
}

/** A heading from the could-not-read card, placed after a colon. Proper nouns keep their capital. */
function afterColon(heading: string): string {
  return /^(Microsoft|Defenssive)\b/.test(heading)
    ? heading
    : heading.charAt(0).toLowerCase() + heading.slice(1)
}

/**
 * An area's status, when it did not complete, as a sentence. 'partial' is the status the scan
 * writes when an area answered but at least one of its checks could not run or was skipped -- and
 * a check not assessed for want of a licence counts as an answer, so a partial area can have had
 * none that ran (review, 2026-09-28): "not all", never "some". A status this build does not know is
 * printed as itself rather than given a friendlier guess.
 */
function notComplete(status: string): string {
  switch (status) {
    case 'failed':
      return 'Could not finish.'
    case 'partial':
      return 'Not all of its checks ran.'
    case 'running':
      return 'Still running.'
    case 'pending':
      return 'Not started.'
    default:
      return `Not complete (${status}).`
  }
}

/**
 * The dashboard's line for an area where some checks saw only part of what they needed, with the
 * checks named: "1 check saw only part of what it needed: Legacy authentication is not blocked."
 * The dashboard's line alone where no check can be named from what the server sent.
 */
function partlyReadSentence(area: PartlyReadArea): string {
  const note = partlyReadNote(area)
  if (area.checks.length === 0) return note

  const names = listOf(area.checks.map(checkName))
  return `${note.replace(/\.$/, '')}: ${names}${/[.!?]$/.test(names) ? '' : '.'}`
}

/**
 * Said where an area finished but the record of which of its checks saw only part of what they
 * needed exists and could not be read. Not "read in full" and not "read only part": the record
 * that would say which could not be read, so neither is known.
 */
const UNCONFIRMED_NOTE = 'Could not confirm that every check in this area read its evidence in full.'

/**
 * Every area this assessment did not fully cover, named, from the same reads the dashboard and the
 * report header use: areas that did not complete, finished areas where some checks saw only part of
 * what they needed (partlyRead.ts, named as the dashboard names them), finished areas whose record of
 * that could not be read, areas and controls not assessed for want of a licence (0158), and controls
 * not assessed for any other reason (0159) -- including those waiting on a change in the tenant (0152).
 *
 * Empty when the assessment covered everything, and when there is no assessment at all (the page
 * says that on its own). Areas in name order; controls with no area last, as "Other".
 */
export function coverageGaps(freshness: ScanFreshness, unread: UnreadControl[]): CoverageGap[] {
  if (freshness.status === null) return []

  const notes = new Map<string, string[]>()
  const add = (area: string, note: string) => {
    const list = notes.get(area)
    if (list) list.push(note)
    else notes.set(area, [note])
  }

  const wholeArea = new Set(notLicensedAreas(freshness))

  // The qualification a finished area carries: every check ran, and some could not read, or could
  // not judge, part of what they needed. In the words the dashboard puts under the area's row, with
  // the checks named -- NOT "read only part of what Microsoft holds", which the dashboard dropped as
  // untrue (a check can read every device and fail to judge some of them).
  const partlyRead = new Map(partlyReadAreas(freshness, { finishedOnly: true }).map((p) => [p.area, p]))
  const unconfirmed = new Set(unconfirmedAreas(freshness))

  for (const area of freshness.areas) {
    // Named below with its reason. It finished only because nothing in it could be read.
    if (wholeArea.has(area.area)) continue

    if (area.status !== 'complete') {
      add(area.area, notComplete(area.status))
      continue
    }

    const partly = partlyRead.get(area.area)
    if (partly) add(area.area, partlyReadSentence(partly))

    // The record existed and could not be read. Without this line the area has no note, and an
    // assessment with nothing else to say would print "Every area was assessed in full" exactly
    // when the record that says otherwise is the thing that could not be read.
    if (unconfirmed.has(area.area)) add(area.area, UNCONFIRMED_NOTE)
  }

  for (const area of wholeArea) {
    add(area, `Not assessed, because this tenant has ${noLicenceFor(notLicensedIn(freshness, area))}.`)
  }

  for (const area of partlyNotLicensedAreas(freshness)) {
    const groups = notLicensedIn(freshness, area)
    add(area, `${controls(controlCount(groups))} not assessed, because this tenant has ${noLicenceFor(groups)}.`)
  }

  // Controls the scan attempted and got no verdict from, by area and then by reason, in the order
  // the dashboard shows its reasons. Waiting controls are their own line: nothing went wrong.
  const byArea = new Map<string, UnreadControl[]>()
  for (const control of unread) {
    const area = control.domain ?? 'Other'
    const list = byArea.get(area)
    if (list) list.push(control)
    else byArea.set(area, [control])
  }

  for (const [area, list] of byArea) {
    const byGroup = new Map<UnreadGroupId, number>()
    let waiting = 0
    for (const control of list) {
      if (isWaiting(control)) {
        waiting++
        continue
      }
      const group = groupOf(control.reason)
      byGroup.set(group, (byGroup.get(group) ?? 0) + 1)
    }

    for (const group of GROUP_ORDER) {
      const n = byGroup.get(group)
      if (!n || group === 'waiting') continue
      add(area, `${controls(n)} not assessed: ${afterColon(describeGroup(group).heading)}.`)
    }

    if (waiting > 0) add(area, `${controls(waiting)} waiting on a change in your tenant, so not assessed.`)
  }

  return [...notes.entries()]
    .map(([area, list]) => ({ area, notes: list }))
    .sort((a, b) => {
      if (a.area === 'Other') return 1
      if (b.area === 'Other') return -1
      return a.area.localeCompare(b.area)
    })
}

/** The class a severity chip takes. An unknown level is drawn like Informational, never coloured. */
function sevClass(severity: string): string {
  return (SEVERITIES as readonly string[]).includes(severity)
    ? `sev-${severity.toLowerCase()}`
    : 'sev-informational'
}

/**
 * How a reopened finding came back, in words. One that reappeared says how it had been closed: a
 * finding closed as not applicable (migration 0176), under the organisation profile or because of the
 * tenant's licences, was never resolved, and "resolved before" would say it was. An older server sends
 * no closedAs, and before 0176 every closure was a resolution.
 */
function howReopened(finding: ChangedFinding): string | null {
  if (finding.reopened === 'reappeared') {
    return finding.closedAs === 'not_applicable'
      ? 'closed as not applicable before, seen again'
      : 'resolved before, seen again'
  }
  if (finding.reopened === 'suppression_expired') return 'its set-aside date passed, seen again'
  return null
}

export function ExecutiveSummary({
  organisation,
  freshness,
  activeBySeverity,
  suppressed,
  unreadControls,
  summary,
}: {
  organisation: string
  freshness: ScanFreshness
  activeBySeverity: Record<string, number>
  suppressed: number
  unreadControls: UnreadControl[]
  summary: SummaryLoad
}) {
  // The same adjustment the header makes: an area complete only because nothing in it is licensed
  // was not assessed, so it is not among the areas "covered".
  const notLicensedCount = notLicensedAreaCount(freshness)
  const gaps = coverageGaps(freshness, unreadControls)

  return (
    <Card className="report-exec">
      <h1 className="report-title">Executive summary</h1>

      <p className="report-exec-lede">
        Microsoft 365 security assessment of <strong>{organisation}</strong>
        {freshness.asOf ? <>, assessed {formatDate(freshness.asOf)}</> : null}. This page is about the whole
        tenant; the detailed report follows it.
      </p>

      {freshness.status === null ? (
        <p className="report-warning">
          No assessment has completed for this tenant{freshness.scanPending ? ' — the first one is queued' : ''},
          so there is nothing to summarise yet.
        </p>
      ) : (
        <>
          {freshness.inconsistent ? (
            <p className="report-warning">
              This assessment is recorded as finished but covered {freshness.areasCompleted} of{' '}
              {freshness.areasTotal} areas. That is a fault on our side.{' '}
              <strong>Do not read this summary as a complete assessment.</strong>
            </p>
          ) : freshness.status === 'complete' && freshness.complete ? null : (
            <p className="report-warning">
              This assessment is <strong>{freshness.status}</strong>: {Math.max(freshness.areasCompleted - notLicensedCount, 0)}{' '}
              of {freshness.areasTotal} areas were covered, so the findings are a floor and not a total.
            </p>
          )}

          <p className="settings-lede report-exec-open">{openFindingsSentence(activeBySeverity, suppressed)}</p>

          <Actions summary={summary} />
          <Changes summary={summary} freshness={freshness} gaps={gaps} />
          <Coverage freshness={freshness} gaps={gaps} />
        </>
      )}
    </Card>
  )
}

function Actions({ summary }: { summary: SummaryLoad }) {
  const actions = summary.state === 'ready' ? summary.data.actions : []

  return (
    <section aria-labelledby="report-exec-actions-heading">
      <h2 id="report-exec-actions-heading" className="report-subheading">
        {actions.length === 5 ? 'Top five actions' : 'Top actions'}
      </h2>

      {summary.state === 'loading' ? <p className="settings-note">Loading…</p> : null}

      {summary.state === 'unavailable' ? (
        <p className="report-warning">
          The actions could not be loaded, so they are not shown here. Nothing else on this page or in
          the report depends on them; reopen the page to try again.
        </p>
      ) : null}

      {summary.state === 'ready' && actions.length === 0 ? (
        <p>No findings are open, so there is no action to list.</p>
      ) : null}

      {actions.length > 0 ? (
        <>
          <p className="settings-note">
            In the order the Recommendations page gives them: quick, high-impact fixes first (“Start
            here”), then by severity and effort. The line under each title is quoted from what the
            finding means.
          </p>
          <ol className="report-exec-actions">
            {actions.map((a) => (
              <li key={a.id}>
                <div className="report-exec-action-title">
                  <span className={`report-sev ${sevClass(a.severity)}`}>{a.severity}</span>
                  <strong>{a.title}</strong>
                </div>
                {a.summary ? <div className="settings-note">{a.summary}</div> : null}
                {a.meaningLine ? <p className="report-exec-meaning">{a.meaningLine}</p> : null}
                <p className="settings-note report-exec-meta">
                  Owner: {ownerText(a.owner)} · {a.area} · {a.effort} effort{a.startHere ? ' · Start here' : ''}
                </p>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </section>
  )
}

function Changes({
  summary,
  freshness,
  gaps,
}: {
  summary: SummaryLoad
  freshness: ScanFreshness
  gaps: CoverageGap[]
}) {
  const changes = summary.state === 'ready' ? summary.data.changes : null

  return (
    <section aria-labelledby="report-exec-changes-heading">
      <h2 id="report-exec-changes-heading" className="report-subheading">Since the previous assessment</h2>

      {summary.state === 'loading' ? <p className="settings-note">Loading…</p> : null}

      {summary.state === 'unavailable' ? (
        <p className="report-warning">
          The changes since the previous assessment could not be loaded, so they are not shown here.
        </p>
      ) : null}

      {summary.state === 'ready' && changes === null ? (
        <p>No assessment has finished yet, so there is nothing to compare.</p>
      ) : null}

      {changes && changes.previous === null ? (
        <p>
          This is the first assessment that finished ({formatDate(changes.assessment.completedAt)}), so
          there is no earlier one to compare it with.
        </p>
      ) : null}

      {changes && changes.previous ? (
        <>
          <p className="settings-note">
            Comparing the assessment that finished on {formatDate(changes.assessment.completedAt)} with the
            one that finished before it, on {formatDate(changes.previous.completedAt)}.
            {changes.assessment.scanId !== freshness.scanId
              ? freshness.status === 'running'
                ? ' The latest assessment is still running, so it is not included.'
                : freshness.status === 'failed'
                  ? ' The latest assessment could not finish, so it is not included.'
                  : ' The latest assessment is not included.'
              : ''}
          </p>

          <table className="settings-table report-exec-changes">
            <thead>
              <tr>
                <th scope="col">Change</th>
                <th scope="col">Count</th>
                <th scope="col">Findings</th>
              </tr>
            </thead>
            <tbody>
              <ChangeRow label="New, still open" list={changes.new} />
              <ChangeRow label="Resolved, not seen since" list={changes.resolved} />
              {/* Closed as not applicable (0176), under the organisation profile or because of the
                  tenant's licences, never under "Resolved": nothing was seen to be fixed. Only when there
                  is one, and absent from an older server. */}
              {changes.noLongerApplies && changes.noLongerApplies.count > 0 ? (
                <ChangeRow label="Closed as not applicable, not counted as fixed" list={changes.noLongerApplies} />
              ) : null}
              <ChangeRow label="Reopened, still open" list={changes.reopened} />
            </tbody>
          </table>

          {/* Every cause, and not one of them picked: the scan stores may_resolve false when no check
              read its evidence in full as well as when it stopped part-way or its access changed, and
              the row does not record which. */}
          {!changes.assessment.mayResolve ? (
            <p className="report-warning">
              This assessment could not mark findings resolved: no check in it read its evidence in full,
              it stopped part-way, or Defenssive’s access changed while it ran. A finding fixed since
              shows as resolved after an assessment that can confirm it.
            </p>
          ) : changes.assessment.status !== 'complete'
            || (changes.assessment.scanId === freshness.scanId && gaps.length > 0) ? (
            <p className="settings-note">
              Only a check that read its evidence in full can mark a finding resolved, so a fix in a part
              this assessment did not fully read shows as resolved after an assessment that reads it.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

function ChangeRow({ label, list }: { label: string; list: ChangeList }) {
  const shown = list.items.slice(0, NAMES_SHOWN)
  const more = list.count - shown.length

  return (
    <tr>
      <th scope="row">{label}</th>
      <td className="mono">{list.count}</td>
      <td>
        {list.count === 0 ? (
          'None'
        ) : (
          <ul className="report-exec-names">
            {shown.map((f: ChangedFinding) => {
              const how = howReopened(f)
              return (
                <li key={f.id}>
                  <span className={`report-sev ${sevClass(f.severity)}`}>{f.severity}</span> {f.title}
                  {how ? <span className="settings-note"> ({how})</span> : null}
                </li>
              )
            })}
            {more > 0 ? <li>and {more} more</li> : null}
          </ul>
        )}
      </td>
    </tr>
  )
}

function Coverage({ freshness, gaps }: { freshness: ScanFreshness; gaps: CoverageGap[] }) {
  const whole = gaps.length === 0 && freshness.status === 'complete' && freshness.complete && !freshness.inconsistent

  return (
    <section aria-labelledby="report-exec-coverage-heading">
      <h2 id="report-exec-coverage-heading" className="report-subheading">Coverage</h2>

      {whole ? (
        <p>Every area was assessed in full.</p>
      ) : gaps.length === 0 ? (
        <p>This assessment did not complete, so it is not a full picture of the tenant.</p>
      ) : (
        <>
          <p className="settings-note">
            What was not assessed is not counted as passing, and no finding in it was closed by this assessment.
          </p>
          <ul className="report-exec-coverage">
            {gaps.map((gap) => (
              <li key={gap.area}>
                <strong>{gap.area}:</strong> {gap.notes.join(' ')}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
